import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { authAPI, locationAPI } from '../api/api';
import { useSocket } from '../context/SocketContext';
import { format } from 'date-fns';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 15); }, [center, map]);
  return null;
}

export default function LocationPage() {
  const { liveLocation } = useSocket();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentLoc, setCurrentLoc] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAPI.children().then(res => {
      setChildren(res.data.children);
      if (res.data.children.length > 0) {
        setSelectedChild(res.data.children[0]);
      }
      setLoading(false);
    });
  }, []);

  const loadLocationData = useCallback(async (childId) => {
    try {
      const [currRes, histRes] = await Promise.all([
        locationAPI.getCurrent(childId),
        locationAPI.getHistory(childId, hours)
      ]);
      if (currRes.data.location) setCurrentLoc(currRes.data.location);
      setHistory(histRes.data.locations || []);
    } catch {}
  }, [hours]);

  useEffect(() => {
    if (selectedChild) loadLocationData(selectedChild._id);
  }, [selectedChild, loadLocationData]);

  // Update currentLoc from live socket data
  useEffect(() => {
    if (selectedChild && liveLocation[selectedChild._id]) {
      setCurrentLoc(liveLocation[selectedChild._id]);
    }
  }, [liveLocation, selectedChild]);

  const center = currentLoc
    ? [currentLoc.latitude, currentLoc.longitude]
    : [20.5937, 78.9629]; // India center

  const historyPath = history.map(l => [l.latitude, l.longitude]);

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📍 Live Location Tracking</h2>
        <select className="form-input form-select" style={{ width: 160 }}
          value={hours} onChange={e => setHours(Number(e.target.value))}>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={48}>Last 48 hours</option>
          <option value={168}>Last 7 days</option>
        </select>
      </div>

      {/* Child Selector */}
      <div className="child-selector">
        {children.map(child => (
          <div key={child._id}
            className={`child-chip ${selectedChild?._id === child._id ? 'active' : ''}`}
            onClick={() => setSelectedChild(child)}
          >
            <div className="child-avatar">{child.name[0]}</div>
            {child.name}
            {liveLocation[child._id] && (
              <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 4 }}>● Live</span>
            )}
          </div>
        ))}
      </div>

      {/* Location Info */}
      {currentLoc && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-icon blue">📍</div>
            <div className="stat-info">
              <div className="stat-value" style={{ fontSize: 14 }}>
                {currentLoc.address || 'Unknown address'}
              </div>
              <div className="stat-label">Current Location</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green">🎯</div>
            <div className="stat-info">
              <div className="stat-value">{Math.round(currentLoc.accuracy || 0)}m</div>
              <div className="stat-label">GPS Accuracy</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange">🏃</div>
            <div className="stat-info">
              <div className="stat-value">
                {currentLoc.speed ? `${(currentLoc.speed * 3.6).toFixed(1)} km/h` : 'Stationary'}
              </div>
              <div className="stat-label">Speed</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon purple">🕐</div>
            <div className="stat-info">
              <div className="stat-value" style={{ fontSize: 14 }}>
                {format(new Date(currentLoc.timestamp), 'HH:mm:ss')}
              </div>
              <div className="stat-label">Last Updated</div>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="map-wrapper">
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {currentLoc && (
              <>
                <MapRecenter center={[currentLoc.latitude, currentLoc.longitude]} />
                <Marker position={[currentLoc.latitude, currentLoc.longitude]}>
                  <Popup>
                    <strong>{selectedChild?.name}</strong><br />
                    {currentLoc.address || 'No address'}<br />
                    {format(new Date(currentLoc.timestamp), 'MMM d, HH:mm:ss')}
                  </Popup>
                </Marker>
              </>
            )}
            {historyPath.length > 1 && (
              <Polyline positions={historyPath} color="#3b82f6" weight={3} opacity={0.7} />
            )}
          </MapContainer>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <div className="card-title">📈 Location History ({history.length} points)</div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Time</th><th>Latitude</th><th>Longitude</th><th>Address</th><th>Type</th></tr></thead>
              <tbody>
                {history.slice(-20).reverse().map((loc, i) => (
                  <tr key={i}>
                    <td>{format(new Date(loc.timestamp), 'MMM d, HH:mm')}</td>
                    <td>{loc.latitude.toFixed(6)}</td>
                    <td>{loc.longitude.toFixed(6)}</td>
                    <td>{loc.address || '—'}</td>
                    <td>{loc.isSOS ? <span className="badge badge-danger">SOS</span> : <span className="badge badge-info">Normal</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
